<template>
    <tr class="domain-row" @click="goToSettings">
        <!-- DOMAIN -->
        <td class="px-6 py-4 min-w-0">
            <div class="flex items-center gap-2 min-w-0">
                <div
                    v-if="domain.active"
                    class="h-2 w-2 rounded-full bg-green-500"
                ></div>
                <div v-else class="h-2 w-2 rounded-full bg-gray-400"></div>
                <span
                    class="font-medium text-blue-600 transition-colors duration-150 hover:text-blue-800 min-w-0 truncate"
                    >{{ domain.domain }}</span
                >
            </div>
        </td>

        <!-- STATUS -->
        <td class="px-6 py-4 whitespace-nowrap min-w-0">
            <span :class="statusBadgeClass">
                {{ statusLabel }}
            </span>
        </td>

        <!-- CREATED -->
        <td class="px-6 py-4 whitespace-nowrap min-w-0">
            <span class="cell-text">
                {{
                    format(new Date(domain.created_at as string), "d. L. yyyy")
                }}
            </span>
        </td>

        <!-- DMARC POLICY -->
        <td class="px-6 py-4 min-w-0">
            <span :class="policyBadgeClass">
                {{ policyLabel }}
            </span>
        </td>

        <!-- ACTIONS -->
        <td class="px-3 py-4 whitespace-nowrap text-center" @click.stop>
            <KebabMenu :use-transition="false" pointer-all>
                <template #default="{ close }">
                    <button
                        v-if="!domain.parked"
                        class="menu-item"
                        role="menuitem"
                        @click="goToReports"
                    >
                        {{ $t("vue.see_reports") }}
                    </button>
                    <button
                        class="menu-item"
                        role="menuitem"
                        @click="goToSettings"
                    >
                        {{ $t("vue.settings") }}
                    </button>
                    <button
                        v-if="!domain.parked"
                        class="menu-item"
                        role="menuitem"
                        @click="goToDmarcManagement"
                    >
                        {{ $t("vue.dmarc_management") }}
                    </button>
                    <button
                        v-if="!domain.parked && !domain.active"
                        class="menu-item"
                        role="menuitem"
                        @click="goToDomainWizard"
                    >
                        {{ $t("vue.domain_wizard") }}
                    </button>
                    <button
                        class="menu-item warning"
                        role="menuitem"
                        @click="
                            () => {
                                close();
                                onParkDomainToggle();
                            }
                        "
                    >
                        {{
                            domain.parked
                                ? $t("vue.unpark_domain")
                                : $t("vue.park_domain")
                        }}
                    </button>
                    <button
                        class="menu-item danger"
                        role="menuitem"
                        @click="
                            () => {
                                close();
                                onDelete();
                            }
                        "
                    >
                        {{ $t("vue.delete_domain") }}
                    </button>
                </template>
            </KebabMenu>
        </td>
    </tr>
</template>

<script lang="ts" setup>
import type { PropType } from "vue";
import { computed } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { format } from "date-fns";
import type IDomain from "@/types/Domains/IDomain";
import { useTranslations } from "@/composables/useTranslations";

const { t } = useTranslations();

const emit = defineEmits([
    "remove",
    "validate",
    "refreshDomains",
    "togglePark",
]);

const props = defineProps({
    domain: { type: Object as PropType<IDomain>, required: true },
    validationLoading: { type: Boolean, default: false },
    dnsChecking: { type: Boolean, default: false },
});

const isParked = computed(() => props.domain.parked === true);

const statusBadgeClass = computed(() => {
    if (isParked.value) return "policy-badge badge-parked";
    return props.domain.active
        ? "policy-badge badge-active"
        : "policy-badge badge-inactive";
});

const statusLabel = computed(() => {
    if (isParked.value) return t("vue.parked");
    return props.domain.active ? t("vue.active") : t("vue.inactive");
});

const policyLabel = computed(() => {
    if (!props.domain.dmarcRecord) return "None";
    const p = (props.domain.dmarcPolicy ?? "none").toLowerCase();
    if (p === "reject") return "Reject";
    if (p === "quarantine") return "Quarantine";
    if (p === "none") return "None";
    return "Unknown";
});

const policyBadgeClass = computed(() => {
    const base = "policy-badge";
    if (!props.domain.dmarcRecord) return `${base} policy-none`;

    const p = (props.domain.dmarcPolicy ?? "none").toLowerCase();
    if (p === "reject") return `${base} policy-reject`;
    if (p === "quarantine") return `${base} policy-quarantine`;
    if (p === "none") return `${base} policy-none`;
    return `${base} policy-none`;
});

const goToReports = () => {
    router.get(route("reports.show", { domain: props.domain.domain }));
};

const goToDomainWizard = () => {
    router.get(route("domainGuide.index", { domain: props.domain.domain }));
};

const goToDmarcManagement = () => {
    router.get(
        route("reports.dmarc-management.show", { domain: props.domain.domain }),
    );
};

const goToSettings = () => {
    const url =
        route("reports.show", { domain: props.domain.domain }) + "/settings";
    router.get(url);
};

const onDelete = () => {
    emit("remove", props.domain.domain);
};

const onParkDomainToggle = () => {
    emit("togglePark", props.domain);
};
</script>

<style>
.domain-row {
    transition: background-color 0.12s ease;
    height: 49.75px;
}

.domain-row > td {
    height: 49.75px;
    padding-top: 6px;
    padding-bottom: 6px;
    vertical-align: middle;
}

.domain-row:hover td {
    background: #fafafa;
    cursor: pointer;
}

.cell-text {
    color: #374151;
}
</style>
